import { useEffect, useRef, useState, type PointerEvent, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import { track, trackFailed } from '../../../../../shared-web/track';
import { useSession } from '../../auth';
import { apiFailureReason, emberApi, type ChatEmberRow, type ChatKind, type ChatRow, type RoomKind } from '../api';
import { useChatFrame } from '../chat-frame';
import { chatCards, chatDate, chatRowTime, hasChatList, introHref, roomTint, speakerSeat, type ChatHere } from '../chats';
import { refreshChats, useChatsOnce, useChatsSnapshot } from '../chats-store';
import { askNotify, notifyState } from '../desktop-notify';
import { BottomSheet } from '../components/BottomSheet';
import { EmberDisc } from '../components/EmberDisc';
import { EmberMascot } from '../components/EmberMascot';
import { PageFailure } from '../components/PageFailure';
import { PageHome } from '../components/PageHome';
import { PageLoading } from '../components/PageLoading';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { navigate } from '../router';
import './Chats.css';

/** Where a row opens: Ember's chat; a room in progress in the chat; an invitation, a wait, a closed room on its page. */
export function chatHref(row: ChatEmberRow | ChatRow): string | null {
  if (row.kind === 'ember') return '/talk?mode=chat';
  if (!row.room_id) return null;
  if (row.kind === 'room') return `/talk?mode=chat&room=${encodeURIComponent(row.room_id)}`;
  // A pair's introduction, and the yes waiting on the other person, open on the introduction page (2026-10-26, New friends).
  // So does one that didn't work out (its row's key is lapsed:<id>): the introduction page says so, and that Ember is still looking.
  if (row.room_kind === 'pair' && (row.kind === 'invite' || row.kind === 'waiting' || row.key.startsWith('lapsed:'))) return introHref(row.room_id);
  return `/rooms/${encodeURIComponent(row.room_id)}`;
}

const MAIL = <svg viewBox="0 0 24 24" aria-hidden><rect x="3.5" y="6" width="17" height="12" rx="2.2" /><path d="m4.5 7.5 7.5 5.6 7.5-5.6" /></svg>;
const CLOCK = <svg viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="12" r="8.2" /><path d="M12 7.6V12l3 1.9" /></svg>;

/**
 * A conversation's picture (the list's rows, the banner, the home bar): Ember's face; a room's people — two of them —
 * huddled with Ember on the room's own colour (2026-10-21 founder); a 1v1, the other person alone (2026-10-27 founder:
 * with Ember beside them it read as a room of two); an envelope for an invitation, a clock for a yes still waiting,
 * Ember on a dashed ring while it looks.
 */
export function ChatPicture({ kind, people = [], roomId = null, roomKind = null }: { kind: ChatKind; people?: Array<{ name: string; avatar: string }>; roomId?: string | null; roomKind?: RoomKind | null }): ReactElement {
  if (kind === 'ember') return <span className="chats-pic is-ember" aria-hidden><EmberDisc className="chats-pic-disc" /></span>;
  if (kind === 'invite') return <span className="chats-pic is-icon is-accent" aria-hidden>{MAIL}</span>;
  if (kind === 'waiting') return <span className="chats-pic is-icon" aria-hidden>{CLOCK}</span>;
  if (kind === 'looking') return <span className="chats-pic is-looking" aria-hidden><EmberMascot face="thinking" still interactive={false} className="chats-pic-look" /></span>;
  if (roomKind === 'pair' && people[0]) return <span className="chats-pic is-person" aria-hidden><span className="chats-face"><PresenceAvatar avatarKey={people[0].avatar} size={52} /></span></span>;
  const faces = people.slice(0, 2);
  return (
    <span className={`chats-pic is-room is-faces-${faces.length + 1} is-tint-${roomTint(roomId)}`} aria-hidden>
      {faces.map((p) => <span key={p.name} className="chats-face"><PresenceAvatar avatarKey={p.avatar} size={26} /></span>)}
      <span className="chats-face"><EmberDisc className="chats-pic-mini" /></span>
    </span>
  );
}

/** The stars over the list: [right, top, size, opacity | twinkle 1 / 2], in px from the header's right and top. */
const STARS: Array<[number, number, number, number | 'tw1' | 'tw2']> = [
  [176, 20, 2, 'tw1'], [138, 48, 1.5, 0.45], [94, 12, 2, 'tw2'], [210, 62, 1.5, 0.35], [46, 74, 2, 'tw1'],
  [80, 98, 1.5, 0.3], [152, 100, 1.5, 0.4], [244, 26, 1.5, 0.3], [22, 112, 1.5, 'tw2'], [190, 122, 1.5, 0.25],
];
const MOON = <svg className="chats-moon" viewBox="0 0 24 24" aria-hidden><path d="M20.5 14.2A8.6 8.6 0 1 1 9.8 3.5a7 7 0 0 0 10.7 10.7z" /></svg>;
/** A cabin on a hill (its own 30×30 box): the walls, a chimney, the roof, the window; the light round it is the box's ::before. */
const CABIN = (
  <svg viewBox="0 0 30 30" aria-hidden>
    <rect className="chats-cabin-wall" x="3" y="12" width="24" height="17" />
    <rect className="chats-cabin-roof" x="21" y="2" width="3.5" height="8" />
    <path className="chats-cabin-roof" d="M0 13 L15 0.5 L30 13 Z" />
    <rect className="chats-cabin-window" x="11" y="17" width="7" height="7" rx="1.2" />
  </svg>
);

/**
 * The list's ground (2026-10-21 founder; mock chats-night-and-fold): a sky over the top — a warm light, fading into
 * the canvas — and two hills at the foot, a cabin with a lit window on each. The stars and the moon live in the
 * header (ChatsHead), so they sit under the Home row wherever the page puts it. Drawn behind everything, decorative.
 */
function ChatsGround(): ReactElement {
  return (
    <div className="chats-ground" aria-hidden>
      <div className="chats-sky" />
      <div className="chats-hills">
        <svg viewBox="0 0 390 170" preserveAspectRatio="none">
          <path className="is-far" d="M0 92 C60 70 120 66 190 84 C260 102 320 104 390 78 L390 170 L0 170 Z" />
          <path className="is-near" d="M0 128 C70 104 150 102 230 118 C310 134 340 132 390 112 L390 170 L0 170 Z" />
        </svg>
        <span className="chats-cabin is-far">{CABIN}</span>
        <span className="chats-cabin is-near">{CABIN}</span>
      </div>
    </div>
  );
}

const PIN = <svg className="chats-pin" viewBox="0 0 24 24" aria-hidden><path d="M14.5 3l6.5 6.5-2.6.9-3.6 3.6.6 4.6-1.6 1.6-3.9-3.9L5 21.2 3 19l4.9-5-3.9-3.9 1.6-1.6 4.6.6 3.6-3.6z" /></svg>;
const LONG_PRESS_MS = 450;

/** A room line's speaker in its row, in the thread's colours: Ember's accent, the person's own ink, the others by seat. */
function speakerClass(who: string, people: Array<{ name: string }>): string {
  if (who === 'Ember') return 'chats-who is-ember';
  if (who === 'You') return 'chats-who';
  return `chats-who is-seat-${(speakerSeat(people, who) % 3) + 1}`;
}

const NOTIFY_ASK_GONE_KEY = 'ember.chats.notify-ask.gone';

/**
 * The one place the web asks for the browser's notifications (2026-10-26 Riley, as WhatsApp Web's bar over its list):
 * a line over the list while the browser can say them and the person has not answered — never asked on its own, only
 * from their tap. "Not now" puts it away on this browser; the browser's own yes or no ends it for good.
 */
function NotifyAsk(): ReactElement | null {
  const [state, setState] = useState(() => notifyState());
  const [gone, setGone] = useState(() => {
    try { return window.localStorage.getItem(NOTIFY_ASK_GONE_KEY) === '1'; } catch { return false; }
  });
  if (state !== 'default' || gone) return null;
  const turnOn = async (): Promise<void> => {
    const answer = await askNotify();
    track('ui.clicked', { name: 'chat_notify_ask', state: answer });
    setState(answer);
  };
  const notNow = (): void => {
    track('ui.clicked', { name: 'chat_notify_ask', state: 'not_now' });
    try { window.localStorage.setItem(NOTIFY_ASK_GONE_KEY, '1'); } catch { /* this visit only */ }
    setGone(true);
  };
  return (
    <div className="chats-notify-ask" data-log-seen="chat_notify_ask">
      <span className="chats-notify-ask-text">Get notified of new messages</span>
      <button type="button" className="chats-notify-ask-on" onClick={() => { void turnOn(); }}>Turn on</button>
      <button type="button" className="chats-notify-ask-close" aria-label="Not now" onClick={notNow}>
        <svg viewBox="0 0 24 24" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </div>
  );
}

/**
 * The Chats list (2026-10-20; mock chats-list-banner-2026-10-20): Ember first, then the person's rooms in the order
 * the server sends — never re-sorted here — on cards (2026-10-21 founder: Ember's own, the rooms they are in, the
 * rest; chatCards). A row opens its conversation; a long press (a right click, or the ⋯ a
 * mouse finds on hover) on a room the person sits in offers Pin to top / Unpin; the looking row opens the note Ember
 * keeps, with Stop looking. A row a line just landed in is lit for a moment. `here` marks the conversation open
 * beside the list (two panes).
 */
export function ChatsList({ here = null }: { here?: ChatHere }): ReactElement | null {
  const { data, arrivals } = useChatsSnapshot();
  const { skin } = useChatFrame();
  const [pinning, setPinning] = useState<ChatRow | null>(null);
  const [looking, setLooking] = useState<ChatRow | null>(null);
  const [lit, setLit] = useState<ReadonlySet<string>>(() => new Set());
  const press = useRef<{ timer: number; x: number; y: number; fired: boolean }>({ timer: 0, x: 0, y: 0, fired: false });

  // A line landed: its row is lit for a moment (the list on screen is told by its rows, not by a banner).
  const litSeq = useRef(arrivals.seq);
  useEffect(() => {
    if (arrivals.seq === litSeq.current) return undefined;
    litSeq.current = arrivals.seq;
    const keys = new Set(arrivals.list.filter((a) => !a.mine).map((a) => a.key));
    if (!keys.size) return undefined;
    setLit(keys);
    const t = window.setTimeout(() => setLit(new Set()), 900);
    return () => window.clearTimeout(t);
  }, [arrivals]);

  if (!data) return null;
  const zone = data.clock.zone;

  const openMenu = (row: ChatEmberRow | ChatRow): void => {
    if (row.kind === 'ember') { toast.info('Ember always stays at the top'); return; }
    if (row.pinnable) setPinning(row);
  };
  const open = (row: ChatEmberRow | ChatRow): void => {
    if (row.kind === 'looking') { setLooking(row); return; }
    const to = chatHref(row);
    if (to && to !== `${window.location.pathname}${window.location.search}`) navigate(to);
  };
  const down = (e: PointerEvent, row: ChatEmberRow | ChatRow): void => {
    if (e.pointerType === 'mouse') return;
    window.clearTimeout(press.current.timer);
    press.current = {
      x: e.clientX, y: e.clientY, fired: false,
      timer: window.setTimeout(() => { press.current.fired = true; openMenu(row); }, LONG_PRESS_MS),
    };
  };
  const moved = (e: PointerEvent): void => {
    if (Math.abs(e.clientX - press.current.x) > 10 || Math.abs(e.clientY - press.current.y) > 10) window.clearTimeout(press.current.timer);
  };
  const up = (): void => window.clearTimeout(press.current.timer);

  const pin = async (row: ChatRow): Promise<void> => {
    setPinning(null);
    if (!row.room_id) return;
    try {
      await emberApi.pinRoom(row.room_id, !row.pinned);
      await refreshChats();
    } catch (err) {
      trackFailed('rooms_load', 'pin_failed', { match_id: row.room_id, error_detail: apiFailureReason(err) });
      toast.error('That didn’t go through. Try again.');
    }
  };
  const stopLooking = async (row: ChatRow): Promise<void> => {
    setLooking(null);
    if (!row.intent_id) return;
    try {
      await emberApi.stopLookingForRoom(row.intent_id);
      await refreshChats();
    } catch (err) {
      trackFailed('rooms_load', 'stop_failed', { match_id: row.intent_id, error_detail: apiFailureReason(err) });
      toast.error('That didn’t go through. Try again.');
    }
  };

  const rowOf = (row: ChatEmberRow | ChatRow): ReactElement => {
    const isRoom = row.kind !== 'ember';
    const pinned = isRoom && row.pinned;
    const roomId = isRoom ? row.room_id : null;
    const isHere = here !== null && (here.kind === 'ember' ? row.kind === 'ember' : roomId === here.roomId);
    const time = chatRowTime(row.at, zone);
    const unread = row.kind === 'invite' ? 0 : row.unread;
    const label = [row.name, pinned ? 'pinned' : '', row.preview, time, unread ? `${unread} unread` : row.kind === 'invite' ? 'new' : '']
      .filter(Boolean).join('. ');
    return (
      <li
        key={row.key}
        className={`chats-row${row.kind === 'closed' ? ' is-closed' : ''}${lit.has(row.key) ? ' is-lit' : ''}${isHere ? ' is-here' : ''}`}
      >
        <button
          type="button"
          className="chats-row-open"
          aria-label={label}
          aria-current={isHere ? 'true' : undefined}
          data-log="chats_open"
          data-log-state={row.kind}
          data-log-match-id={(isRoom ? row.room_id || row.intent_id : null) || undefined}
          onClick={() => {
            if (press.current.fired) { press.current.fired = false; return; }
            open(row);
          }}
          onPointerDown={(e) => down(e, row)}
          onPointerMove={moved}
          onPointerUp={up}
          onPointerCancel={up}
          onPointerLeave={up}
          onContextMenu={(e) => { e.preventDefault(); window.clearTimeout(press.current.timer); openMenu(row); }}
        >
          <ChatPicture kind={row.kind} people={isRoom ? row.people : []} roomId={roomId} roomKind={isRoom ? row.room_kind : null} />
          <span className="chats-text">
            <span className="chats-name"><span dir="auto">{row.name}</span>{pinned && PIN}</span>
            <span className={`chats-preview${row.kind === 'invite' ? ' is-accent' : ''}`} dir="auto">
              {row.kind === 'room' && row.last ? <><b className={speakerClass(row.last.who, row.people)}>{row.last.who}:</b> {row.last.text}</> : row.preview}
            </span>
          </span>
          <span className="chats-meta">
            <span className={`chats-time${unread > 0 ? ' is-hot' : ''}`}>{time}</span>
            {unread > 0 ? <span className="chats-count">{unread > 99 ? '99+' : unread}</span>
              : row.kind === 'invite' ? <span className="chats-dot" /> : null}
          </span>
        </button>
        {isRoom && row.pinnable && (
          <button type="button" className="chats-row-more" aria-label={`More for ${row.name}`} onClick={() => setPinning(row)}>
            <svg viewBox="0 0 24 24" aria-hidden><circle cx="5.5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="18.5" cy="12" r="1.4" /></svg>
          </button>
        )}
      </li>
    );
  };
  const { rooms, rest } = chatCards(data.chats);

  return (
    <>
      <section className="chats-rows" aria-label="Chats">
        <NotifyAsk />
        <ul className="chats-card is-ember">{rowOf(data.ember)}</ul>
        {rooms.length > 0 && <ul className="chats-card">{rooms.map(rowOf)}</ul>}
        {rest.length > 0 && <ul className="chats-card">{rest.map(rowOf)}</ul>}
      </section>
      {pinning && (
        <BottomSheet title={pinning.name} onClose={() => setPinning(null)} skin={skin}>
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="chats_pin"
            data-log-state={pinning.pinned ? 'unpin' : 'pin'}
            data-log-match-id={pinning.room_id || undefined}
            onClick={() => { void pin(pinning); }}
          >
            {pinning.pinned ? 'Unpin' : 'Pin to top'}
          </button>
        </BottomSheet>
      )}
      {looking && (
        <BottomSheet
          skin={skin}
          title="Ember is looking for a room for you"
          description={(
            <>
              <span className="chats-look-note" dir="auto">{looking.preview}</span>
              {looking.at && <span className="chats-look-since">since {chatDate(looking.at, zone)} · for a week</span>}
            </>
          )}
          onClose={() => setLooking(null)}
        >
          <button
            type="button"
            className="ember-btn ember-btn-secondary"
            data-log="chats_stop_looking"
            data-log-match-id={looking.intent_id || undefined}
            onClick={() => { void stopLooking(looking); }}
          >
            Stop looking
          </button>
        </BottomSheet>
      )}
    </>
  );
}

/** The header: Home, then the large title; at night a moon beside Home and a few stars round them (none by day). */
function ChatsHead(): ReactElement {
  return (
    <header className="chats-head">
      <span className="chats-stars" aria-hidden>
        {STARS.map(([right, top, size, look]) => (
          <span
            key={`${right}:${top}`}
            className={`chats-star${typeof look === 'string' ? ` is-${look}` : ''}`}
            style={{ right, top, width: size, height: size, opacity: typeof look === 'number' ? look : undefined }}
          />
        ))}
      </span>
      <span className="chats-head-row"><PageHome className="chats-home" />{MOON}</span>
      <h1 className="chats-title">Chats</h1>
    </header>
  );
}

/**
 * /chats (2026-10-20): the list on its own screen, every signed-in person's (2026-10-21 Riley — until then only
 * someone with something past Ember; with no room the list is Ember's row alone). A guest is sent to sign in and
 * lands back here (returnTo); beside a desktop's two panes the list already stands on the left, so the page is the
 * conversation with Ember there.
 */
export function ChatsPage(): ReactElement {
  const { status } = useSession();
  const { skin, split } = useChatFrame();
  const listed = hasChatList(status);
  // Read as it opens when the last read is older than a poll (2026-10-31 founder: a page reads fresh when it is shown) —
  // a read that failed, or a change nobody announced, is put right by coming to the list. Beside two panes the pane reads.
  const { data, failed } = useChatsOnce(listed && !split);
  useEffect(() => {
    if (status === 'anonymous') navigate('/auth?returnTo=%2Fchats', { replace: true });
    else if (split) navigate('/talk?mode=chat', { replace: true });
  }, [status, split]);

  if (!data && failed) return <PageFailure title="Couldn’t open your chats." detail="Try again in a moment." logState="load_failed" />;
  return (
    <div className="chats-page" data-chat-skin={skin}>
      <ChatsGround />
      <ChatsHead />
      {listed && data ? <ChatsList /> : <PageLoading variant="inline" className="chats-loading" />}
    </div>
  );
}

/** The list beside the conversation on a wide window (two panes), read as it opens as the page is. */
export function ChatsPane({ here }: { here: ChatHere }): ReactElement {
  const { skin } = useChatFrame();
  useChatsOnce(true);
  return (
    <aside className="chats-pane" data-chat-skin={skin} aria-label="Your chats">
      <ChatsGround />
      <ChatsHead />
      <ChatsList here={here} />
    </aside>
  );
}
